package com.zq.webproject.network

import com.zq.webproject.model.AddBankCardResponse
import com.zq.webproject.model.AppVersion
import com.zq.webproject.model.ApplyWithdrawResponse
import com.zq.webproject.model.ComplaintResponse
import com.zq.webproject.model.CreateOrderResponse
import com.zq.webproject.model.IncomeRecordResponse
import com.zq.webproject.model.InvitedUsersResponse
import com.zq.webproject.model.OrderDetailResponse
import com.zq.webproject.model.ProductDetailResponse
import com.zq.webproject.model.PurchaseRecordResponse
import com.zq.webproject.model.RankingResponse
import com.zq.webproject.model.WithdrawRecordListResponse
import com.zq.webproject.network.model.BalanceResponse
import com.zq.webproject.network.model.BannerResponse
import com.zq.webproject.network.model.CategoryResponse
import com.zq.webproject.network.model.EarningsResponse
import com.zq.webproject.network.model.LoginResponse
import com.zq.webproject.network.model.ProductResponse
import com.zq.webproject.network.model.RegisterResponse
import com.zq.webproject.network.model.RechargeResponse
import com.zq.webproject.network.model.SendCodeResponse
import com.zq.webproject.network.model.UpgradeResponse
import com.zq.webproject.network.model.UploadProductResponse
import com.zq.webproject.network.model.UserRoleResponse
import com.zq.webproject.network.network.NetworkService
import org.example.project.model.AlipayResponse
import org.example.project.model.PaymentResponse


class Repository {
    private val networkService = NetworkService()


    /**
     * 发送验证码
     * @param phone 手机号
     * @return Result<SendCodeResponse> 发送结果
     */
    suspend fun sendCode(phone: String): Result<SendCodeResponse> {
        return networkService.sendCode(phone)
    }


    /**
     * 注册
     * @param phone 手机号
     * @param password 密码
     * @param code 验证码
     * @param invitationCode 邀请码(非必填)
     * @param deviceId 设备唯一标识
     * @param deviceName 设备名称（可选）
     * @param deviceType 设备类型（可选）
     * @return Result<RegisterResponse> 注册结果
     */
    suspend fun register(
        phone: String,
        password: String,
        code: String,
        invitationCode: String? = null,
        deviceId: String,
        deviceName: String? = null,
        deviceType: String? = null
    ): Result<RegisterResponse> {
        return networkService.register(phone, password, code, invitationCode, deviceId, deviceName, deviceType)
    }


    /**
     * 登录
     * @param username 用户名
     * @param password 密码
     * @param deviceId 设备唯一标识
     * @param deviceName 设备名称（可选）
     * @param deviceType 设备类型（可选）
     * @return Result<LoginResponse> 登录结果
     */
    suspend fun login(
        username: String, 
        password: String,
        deviceId: String,
        deviceName: String? = null,
        deviceType: String? = null
    ): Result<LoginResponse> {
        return networkService.login(username, password, deviceId, deviceName, deviceType)
    }


    /**
     * 获取用户信息
     * @param token 用户Token
     * @return Result<String> 用户信息
     */
    suspend fun getUserProfile(token: String): Result<String> {
        return networkService.getUserProfile(token)
    }


    /**
     * 获取商品分类列表
     * @param token 用户Token
     * @return Result<CategoryResponse> 分类结果
     */
    suspend fun getCategories(token: String): Result<CategoryResponse> {
        return networkService.getCategories(token)
    }


    /**
     * 获取商品列表（分页）
     * @param token 用户Token
     * @param page 页码
     * @param pageSize 每页数量
     * @return Result<ProductResponse> 商品列表结果
     */
    suspend fun getProducts(
        token: String,
        page: String = "1",
        pageSize: String = "100"
    ): Result<ProductResponse> {
        return networkService.getProducts(token, page, pageSize)
    }


    /**
     * 获取首页Banner图
     * @param token 用户Token
     * @return Result<BannerResponse> Banner结果
     */
    suspend fun getHomeBanners(token: String): Result<BannerResponse> {
        return networkService.getHomeBanners(token)
    }

    /**
     * 检查应用版本更新
     * @return Result<AppVersion> 版本信息结果
     */
    suspend fun checkAppVersion(): Result<AppVersion> {
        return networkService.checkAppVersion()
    }


    /**
     * 根据分类ID筛选商品（支持分页）
     * @param token 用户Token
     * @param categoryId 分类ID
     * @param page 页码（默认1）
     * @param pageSize 每页数量（默认50）
     * @return Result<ProductResponse> 对应分类的商品列表
     */
    suspend fun getProductsByCategory(
        token: String,
        categoryId: String,
        page: String = "1",
        pageSize: String = "50"
    ): Result<ProductResponse> {
        return networkService.getProductsByCategory(token, categoryId, page, pageSize)
    }


    /**
     * 关闭网络连接
     */
    fun close() {
        networkService.close()
    }


    /**
     * 获取用户余额
     * @param token 用户Token
     * @return Result<BalanceResponse> 余额结果
     */
    suspend fun getUserBalance(token: String): Result<BalanceResponse> {
        return networkService.getUserBalance(token)
    }


    /**
     * 充值
     * @param token 用户Token
     * @param amount 充值金额
     * @param paymentMethod 支付方式：alipay(支付宝支付)
     * @return Result<RechargeResponse> 充值结果
     */
    suspend fun rechargeBalance(token: String, amount: Double, paymentMethod: String = "alipay"): Result<RechargeResponse> {
        println("Repository - 调用 NetworkService.rechargeBalance(), 金额: $amount, 支付方式: $paymentMethod") // 日志输出
        return networkService.rechargeBalance(token, amount, paymentMethod)
    }

    /**
     * 升级为加盟商
     * @param token 用户Token
     * @param paymentMethod 支付方式：balance(余额支付) 或 alipay(支付宝支付)
     * @return Result<UpgradeResponse> 升级结果
     */
    suspend fun upgradeToAffiliate(token: String, paymentMethod: String = "balance"): Result<UpgradeResponse> {
        println("Repository - 调用 NetworkService.upgradeToAffiliate(), 支付方式: $paymentMethod") // 日志输出
        return networkService.upgradeToAffiliate(token, paymentMethod)
    }


    /**
     * 获取用户角色
     * @param token 用户Token
     * @return Result<UserRoleResponse> 角色结果
     */
    suspend fun getUserRole(token: String): Result<UserRoleResponse> {
        return networkService.getUserRole(token)
    }

    /**
     * 获取用户总收益
     * @param token 用户Token
     * @return Result<EarningsResponse> 总收益结果
     */
    suspend fun getUserEarnings(token: String): Result<EarningsResponse> {
        println("Repository - 调用 NetworkService.getUserEarnings()") // 日志输出
        return networkService.getUserEarnings(token)
    }


    /**
     * 获取商品详情
     * @param productId 商品ID
     * @return Result<ProductDetailResponse> 商品详情结果
     */
    suspend fun getProductDetail(productId: String): Result<ProductDetailResponse> {
        return networkService.getProductDetail(productId)
    }


    /**
     * 创建订单
     * @param productId 商品ID
     * @param token 用户Token
     * @return Result<CreateOrderResponse> 订单创建结果
     */
    suspend fun createOrder(productId: Int, token: String): Result<CreateOrderResponse> {
        return networkService.createOrder(productId, token)
    }


    /**
     * 获取订单详情
     * @param orderNo 订单号
     * @param token 用户Token
     * @return Result<OrderDetailResponse> 订单详情结果
     */
    suspend fun getOrderDetail(orderNo: String, token: String): Result<OrderDetailResponse> {
        return networkService.getOrderDetail(orderNo, token)
    }

    /**
     * 余额支付
     * @param amount 支付金额
     * @param orderNo 订单号
     * @param token 用户Token
     * @return Result<PaymentResponse> 支付结果
     */
    suspend fun payWithBalance(amount: Double, orderNo: String, token: String): Result<PaymentResponse> {
        return networkService.payWithBalance(amount, orderNo, token)
    }

    /**
     * 获取个人购买记录
     * @return PurchaseRecordResponse 购买记录结果
     */
    suspend fun getUserPurchaseHistory(): PurchaseRecordResponse {
        println("Repository - 调用 NetworkService.getUserPurchaseHistory()") // 日志输出
        return networkService.getUserPurchaseHistory()
    }

    /**
     * 添加银行卡
     * @param token 用户Token
     * @param cardNumber 银行卡号
     * @param cardHolderName 持卡人姓名
     * @param expirationDate 有效期
     * @param cvv CVV安全码
     * @param bankName 银行名称
     * @return Result<AddBankCardResponse> 添加结果
     */
    suspend fun addBankCard(
        token: String,
        cardNumber: String,
        cardHolderName: String,
        expirationDate: String,
        cvv: String,
        bankName: String
    ): Result<AddBankCardResponse> {
        println("Repository - 调用 NetworkService.addBankCard()") // 日志输出
        return networkService.addBankCard(token, cardNumber, cardHolderName, expirationDate, cvv, bankName)
    }

    /**
     * 申请提现
     * @param token 用户Token
     * @param amount 提现金额
     * @return Result<ApplyWithdrawResponse> 申请结果
     */
    suspend fun applyWithdraw(token: String, amount: Double): Result<ApplyWithdrawResponse> {
        println("Repository - 调用 NetworkService.applyWithdraw(), 金额: $amount") // 日志输出
        return networkService.applyWithdraw(token, amount)
    }

    /**
     * 获取用户收益记录
     * @param token 用户Token
     * @param page 页码（默认1）
     * @param pageSize 每页数量（默认10）
     * @return Result<IncomeRecordResponse> 收益记录结果
     */
    suspend fun getIncomeRecords(token: String, page: Int = 1, pageSize: Int = 10): Result<IncomeRecordResponse> {
        println("Repository - 调用 NetworkService.getIncomeRecords(), 页码: $page, 每页: $pageSize") // 日志输出
        return networkService.getIncomeRecords(token, page, pageSize)
    }

    /**
     * 获取邀请用户列表
     * @param token 用户Token
     * @return Result<InvitedUsersResponse> 邀请用户列表结果
     */
    suspend fun getInvitedUsers(token: String): Result<InvitedUsersResponse> {
        println("Repository - 调用 NetworkService.getInvitedUsers()") // 日志输出
        return networkService.getInvitedUsers(token)
    }

    /**
     * 获取收入排行榜
     * @param token 用户Token
     * @return Result<RankingResponse> 排行榜结果
     */
    suspend fun getIncomeRanking(token: String): Result<RankingResponse> {
        println("Repository - 调用 NetworkService.getIncomeRanking()") // 日志输出
        return networkService.getIncomeRanking(token)
    }

    /**
     * 获取提现记录列表
     * @param token 用户Token
     * @return Result<WithdrawRecordListResponse> 提现记录列表结果
     */
    suspend fun getWithdrawalRecords(token: String): Result<WithdrawRecordListResponse> {
        println("Repository - 调用 NetworkService.getWithdrawalRecords()") // 日志输出
        return networkService.getWithdrawalRecords(token)
    }
    /**
     * 支付宝支付
     * @param productId 商品ID
     * @param paymentType 支付类型（alipay）
     * @param quantity 数量
     * @param token 用户Token
     * @return Result<AlipayResponse> 支付宝支付结果
     */
    suspend fun createAlipayOrder(
        productId: Int,
        paymentType: String = "alipay",
        quantity: Int = 1,
        token: String
    ): Result<AlipayResponse> {
        println("Repository - 调用 NetworkService.createAlipayOrder()") // 日志输出
        return networkService.createAlipayOrder(productId, paymentType, quantity, token)
    }
    
    /**
     * 升级管理员支付
     * @param token 用户Token
     * @return Result<AlipayResponse> 支付宝支付结果
     */
    suspend fun upgradeToAdmin(token: String): Result<AlipayResponse> {
        println("Repository - 调用 NetworkService.upgradeToAdmin()") // 日志输出
        return networkService.upgradeToAdmin(token)
    }
    
    /**
     * 提交商品投诉
     * @param productId 商品ID
     * @param productName 商品名称
     * @param productPrice 商品价格
     * @param complaintReason 投诉理由
     * @param userId 用户ID（可选）
     * @param userName 用户名（可选）
     * @return Result<ComplaintResponse> 投诉提交结果
     */
    suspend fun submitComplaint(
        productId: String,
        productName: String,
        productPrice: Double,
        complaintReason: String,
        userId: Int? = null,
        userName: String? = null
    ): Result<ComplaintResponse> {
        println("Repository - 调用 NetworkService.submitComplaint()") // 日志输出
        return networkService.submitComplaint(
            productId, productName, productPrice, complaintReason, userId, userName
        )
    }
    
    /**
     * 检查升级管理员支付状态
     * @param token 用户Token
     * @return Result<Map<String, Any?>> 检查结果
     */
    suspend fun checkUpgradeStatus(token: String): Result<Map<String, Any?>> {
        println("Repository - 调用 NetworkService.checkUpgradeStatus()") // 日志输出
        return networkService.checkUpgradeStatus(token)
    }

    /**
     * 上传商品
     * @param name 商品名称
     * @param linkUrl 资源链接地址
     * @param category 商品分类
     * @param remark 备注信息
     * @param token 用户Token
     * @return Result<UploadProductResponse> 上传结果
     */
    suspend fun uploadProduct(
        name: String,
        linkUrl: String,
        category: String,
        remark: String?,
        token: String
    ): Result<UploadProductResponse> {
        println("Repository - 调用 NetworkService.uploadProduct()")
        println("Repository - 商品信息: name=$name, category=$category")
        return networkService.uploadProduct(name, linkUrl, category, remark, token)
    }
}